import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Copy, Instagram } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import KeyButton from "@/components/KeyButton";

const EASE = [0.16, 1, 0.3, 1];
const EMAIL = "aashish@leevemedia.com";
const INSTAGRAM = "https://www.instagram.com/leeve.media/";
const API = "/api";

const EMPTY = { name: "", business: "", phone: "", type: "", budget: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      toast.success("Email copied to clipboard");
    } catch {
      toast.error("Could not copy — " + EMAIL);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Enquiry sent — we'll be in touch soon");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong — please email us directly");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full border border-[#0D0D0D]/20 bg-transparent px-4 py-3 text-sm text-[#0D0D0D] placeholder-[#8C8B85] outline-none transition-colors duration-300 focus:border-[#0D0D0D]";

  const selectWrap = "relative";
  const selectField = (value) =>
    `${field} appearance-none pr-10 ${value ? "" : "text-[#8C8B85]"}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-[#0D0D0D]/10 px-6 py-24 sm:px-10 sm:py-32"
    >
      <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-2 lg:gap-20">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="font-mono-label text-[10px] uppercase tracking-[0.3em] text-[#4A4944] sm:text-xs"
          >
            (05) — Contact
          </motion.p>
          <motion.h2
            data-testid="contact-heading"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="mt-8 font-helvetica text-5xl font-bold leading-[1.02] tracking-tight text-[#0D0D0D] sm:text-6xl lg:text-7xl"
          >
            Have
            <br />
            Questions?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="mt-6 font-mono-label text-[10px] uppercase tracking-[0.3em] text-[#8C8B85]"
          >
            Contact us — we reply fast
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
            className="mt-10 space-y-2"
          >
            <a
              data-testid="contact-phone-1"
              href="tel:+919448629894"
              className="block font-helvetica text-2xl font-bold tracking-tight text-[#0D0D0D] transition-colors duration-300 hover:text-[#8C8B85] sm:text-3xl"
            >
              +91 94486 29894
            </a>
            <a
              data-testid="contact-phone-2"
              href="tel:+919110688672"
              className="block font-helvetica text-2xl font-bold tracking-tight text-[#0D0D0D] transition-colors duration-300 hover:text-[#8C8B85] sm:text-3xl"
            >
              +91 91106 88672
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="mt-10"
          >
            <KeyButton testid="contact-email-copy-button" onClick={copyEmail}>
              <span className="flex items-center gap-3">
                {EMAIL}
                <Copy size={14} />
              </span>
            </KeyButton>
            <a
              data-testid="contact-instagram-link"
              href={INSTAGRAM}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-6 flex w-fit items-center gap-3"
            >
              <span
                className="flex h-9 w-9 items-center justify-center rounded-[9px] shadow-[0_6px_14px_-6px_rgba(214,41,118,0.5)]"
                style={{
                  background:
                    "linear-gradient(45deg,#feda75 0%,#fa7e1e 30%,#d62976 60%,#962fbf 85%,#4f5bd5 100%)",
                }}
              >
                <Instagram size={17} className="text-white" />
              </span>
              <span className="font-helvetica text-lg font-bold tracking-tight text-[#0D0D0D]">
                @leeve.media
              </span>
              <ArrowUpRight
                size={14}
                className="text-[#8C8B85] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </motion.div>
        </div>

        <motion.form
          data-testid="contact-inquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="space-y-5 self-start"
        >
          <input
            data-testid="contact-input-name"
            required
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={field}
          />
          <input
            data-testid="contact-input-business"
            required
            placeholder="Your business's name"
            value={form.business}
            onChange={(e) => setForm({ ...form, business: e.target.value })}
            className={field}
          />
          <input
            data-testid="contact-input-phone"
            required
            type="tel"
            placeholder="Phone number"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className={field}
          />
          <div className={selectWrap}>
            <select
              data-testid="contact-input-type"
              required
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className={selectField(form.type)}
            >
              <option value="" disabled>
                I am a…
              </option>
              <option value="creator">Creator</option>
              <option value="business">Business</option>
            </select>
            <ChevronDown
              size={15}
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#8C8B85]"
            />
          </div>
          <div className={selectWrap}>
            <select
              data-testid="contact-input-budget"
              required
              value={form.budget}
              onChange={(e) => setForm({ ...form, budget: e.target.value })}
              className={selectField(form.budget)}
            >
              <option value="" disabled>
                Your budget
              </option>
              <option value="40k/month">₹40K / month</option>
              <option value="60k/month">₹60K / month</option>
              <option value="80k/month">₹80K / month</option>
              <option value="not-sure">Not sure — one of the above</option>
              <option value="higher-deliverables">
                Require higher deliverables than the highest package
              </option>
            </select>
            <ChevronDown
              size={15}
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#8C8B85]"
            />
          </div>
          <KeyButton
            testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="w-full"
          >
            {sending ? "SENDING…" : "SEND ENQUIRY"}
          </KeyButton>
        </motion.form>
      </div>
    </section>
  );
}
